// src/clock/render.js — clock-screen rendering (v88.0)
//
// Extracted from index.html in v88.0. Previously lived at lines 5459–5755
// in v87.1 — the two top-level render functions that paint the clock screen:
//
//   renderMultiStoreClock — area manager view (multiple stores, table per store)
//   renderSingleStoreClock — staff view (one store, one card per person)
//
// A dispatcher in index.html chooses between them based on isAreaUser:
//
//   if (isAreaUser) Clock.renderMulti(); else Clock.renderSingle();
//
// Why both renderers live in one module: they read the same state
// (Clock.events, Clock.history), share the same per-store roster logic
// (getStoreStaff, getShift), and the inline UI experiments (chips,
// punctuality badges) historically copy-pasted between them. Co-locating
// makes future shared-helper extractions obvious.
//
// Imports:
//   - clockEvents, getClockEventForDate from state.js → read the live + history
//     clock data the renderers paint
//   - renderLocationBanner from location.js → single-store header calls this
//     each render so the banner stays current with permission state
//   - openClockDetail from detail.js → the card click handler in the
//     single-store renderer opens the detail sheet
//
// Free-variable dependencies (resolved from window at call time):
//   getMyStores, getBusinessDay, getDayKey, getWeekStart, getStoreStaff,
//   getStoreCountry, getShift, buildFirstNameMap, computeDisplayNames,
//   localDateStr, timeMins, initials, escapeHtml, openTimeCardAction,
//   ALL_STAFF, currentStore, shifts, COLORS. All live as inline globals
//   in index.html.
//
// CATEGORY 1 — DELIBERATE PRODUCT RULES (do not "simplify"):
//
//   v48:    Cross-store roster. The single-store renderer scans the week's
//           shifts[] for any with workStore===currentStore whose staff is
//           home-based elsewhere — these appear as covers. Home staff
//           scheduled at OTHER stores today are excluded. The intent is
//           that what's drawn matches what's physically at the till.
//
//   v80.15: Business-day default. When no history date is picked, both
//           renderers use getBusinessDay() (3am–3am window), not calendar
//           day. A staffer working 15:00–00:00 still sees Friday's roster
//           at 00:30 instead of an empty Saturday.
//
//   v80.8:  Break-clockout confirmation flow. Live cards show "End Break"
//           as the prominent action when ev.status==='break' — accidental
//           clock_outs from break state caused real bugs (Wafi City, 5 May).
//           The detail sheet adds the "Are you sure?" confirm. Don't
//           collapse the two states back together.
//
// CATEGORY 2 — DEFENSIVE LOGIC FROM OBSERVED BUGS:
//
//   v79.15.1: Past-date renders route through getClockEventForDate (per-date
//             cache) instead of getClockForWeek. Without this, history views
//             outside the current week showed every staff row blank.
//   v80.16:   First-name disambiguation via buildFirstNameMap — two Annas
//             on one rota became indistinguishable; the map adds initials
//             only when needed.

import { clockEvents, getClockEventForDate } from './state.js';
import { renderLocationBanner } from './location.js';
import { openClockDetail } from './detail.js';

// v123.x — the day's SHIFT roster for one store: everyone physically scheduled
// HERE today. Home staff, EXCEPT anyone whose shift today is a cover at another
// store; PLUS cover staff whose shift today has workStore === this store. Shared
// by both the single-store and the All-Stores (HO) clock views so they can't
// diverge — the multi-store (HO) view used to just use getStoreStaff() (home
// only), so a staffer covering elsewhere still showed under their HOME store and
// a cover was missing from the store they were actually at (the headcount bug).
export function buildStoreShiftRoster(store, today, weekStart){
  var homeStaff = (typeof getStoreStaff === 'function') ? getStoreStaff(store) : [];
  var wkStr = localDateStr(weekStart);
  var roster = [], seen = {};
  // v129.156 (split shifts) — a person's day can have a SECOND leg (shifts2).
  // Someone with EITHER leg at this store belongs on this store's clock roster
  // — without this, a stocktake crew member whose leg 2 is here would have no
  // card at this till and could not clock in for the night leg at all.
  var leg2AtStore = function(sid){
    var s2 = (typeof getShift2 === 'function') ? getShift2(sid, today, weekStart) : undefined;
    return !!(s2 && s2.type === 'work' && s2.workStore === store);
  };
  homeStaff.forEach(function(s){
    var sh = getShift(s.id, today, weekStart);
    // home staffer covering at ANOTHER store today → don't show them here
    // (UNLESS their second leg is here — a split day keeps them on this roster)
    if (sh && sh.type === 'work' && sh.workStore && sh.workStore !== store && !leg2AtStore(s.id)) return;
    roster.push(s); seen[s.id] = true;
  });
  // cover staff whose shift today is at THIS store
  Object.keys(shifts).forEach(function(key){
    if (key.indexOf(wkStr + '_') !== 0) return;
    if (!key.endsWith('_' + today)) return;
    var sh = shifts[key];
    if (!sh || sh.workStore !== store) return;
    var parts = key.split('_');
    var sid = parseInt(parts[3] || parts[parts.length - 2]);
    if (isNaN(sid) || seen[sid]) return;
    var s = ALL_STAFF.find(function(x){ return x.id === sid; });
    if (s) { roster.push(s); seen[sid] = true; }
  });
  // v129.156 — cover staff whose SECOND leg today is at this store (the
  // stocktake case: leg 1 at home, leg 2 here — they need a card HERE).
  if (typeof shifts2 !== 'undefined') {
    Object.keys(shifts2).forEach(function(key){
      if (key.indexOf(wkStr + '_') !== 0) return;
      if (!key.endsWith('_' + today)) return;
      var sh = shifts2[key];
      if (!sh || sh.type !== 'work' || sh.workStore !== store) return;
      var parts = key.split('_');
      var sid = parseInt(parts[3] || parts[parts.length - 2]);
      if (isNaN(sid) || seen[sid]) return;
      var s = ALL_STAFF.find(function(x){ return x.id === sid; });
      if (s) { roster.push(s); seen[sid] = true; }
    });
  }
  return roster;
}

export function renderMultiStoreClock(){
  var myStores=getMyStores();
  var dateInput=document.getElementById('clockHistoryDate');
  // v80.15: see renderSingleStoreClock — business-day default for "live" view.
  var viewingDate=dateInput&&dateInput.value?new Date(dateInput.value):getBusinessDay();
  var today=getDayKey(viewingDate);
  var weekStart=getWeekStart(viewingDate);
  var isToday=!dateInput||!dateInput.value;
  // v79.15.1: localDateStr (NOT toISOString — UTC drift bites at +4 in the evening).
  // Used to look up per-date clock history via getClockEventForDate.
  var viewingDateStr=localDateStr(viewingDate);
  document.getElementById('clockDate').textContent=viewingDate.toLocaleDateString('en-GB',{weekday:'long',day:'numeric',month:'long'})+(isToday?' — Live':' (history)');

  var totalIn=0,totalBreak=0,totalLate=0;
  var html='';

  myStores.forEach(function(store){
    // v123.x: cover-aware roster (was getStoreStaff = home only), so a staffer
    // covering elsewhere no longer shows under their home store here, and a cover
    // appears under the store they're actually at.
    var staff=buildStoreShiftRoster(store,today,weekStart);
    if(!staff.length)return;
    // v80.16: disambiguate first names within this store's roster
    var nameMap = buildFirstNameMap(staff);
    var isQ=getStoreCountry(store)==='ELC - Qatar';
    var storeIn=0,storeBreak=0,storeOff=0,storeExpected=0;

    var rows='';
    staff.forEach(function(s){
      var sh=getShift(s.id,today,weekStart);
      // v129.18: 'apl' added — someone on Authorised Paid Leave was counted in
      // the store's EXPECTED headcount all day instead of as off.
      // v129.21: 'oos' added too. The v129.18 sweep fixed the OTHER list in
      // this file (:253) but left this one a value short — the same one-list-
      // of-two drift, one release later. Someone marked "Not in Store" was
      // counted into storeExpected and could be flagged LATE at a store they
      // are not at.
      if(sh&&(sh.type==='off'||sh.type==='holiday'||sh.type==='sick'||sh.type==='toil'||sh.type==='apl'||sh.type==='oos')){storeOff++;return;}
      // v79.15.1: route past dates through getClockEventForDate (which reads
      // from the per-date cache populated by loadClockFromDB). The old path
      // via getClockForWeek returned null outside the current week, blanking
      // every staff row on past-date views.
      var ev=isToday?(clockEvents[s.id]||{}):(getClockEventForDate(s.id,viewingDateStr)||{});
      var status='expected',statusLabel='Expected',statusColor='#f59e0b',statusBg='var(--warn-bg)';
      if(ev.clockIn&&ev.clockOut){status='out';statusLabel='Clocked out';statusColor='var(--sub)';statusBg='var(--surface-3)';}
      else if(ev.clockIn&&ev.breakStart&&!ev.breakEnd){status='break';statusLabel='On break';statusColor='var(--teal)';statusBg='var(--rota-teal-bg)';storeBreak++;}
      else if(ev.clockIn){status='in';statusLabel='On floor';statusColor='var(--rota-green-ink)';statusBg='var(--ok-bg)';storeIn++;}
      else{storeExpected++;}
      if(sh&&sh.start){
        var nowM=new Date().getHours()*60+new Date().getMinutes();
        var schedM=timeMins(sh.start);
        if(isToday&&!ev.clockIn&&nowM>schedM+5){totalLate++;statusLabel='Late';statusColor='var(--rota-red-ink)';statusBg='var(--danger-bg)';}
      }
      rows+='<tr style="border-bottom:1px solid var(--border)"><td style="padding:5px 6px;font-weight:600;font-size:12px" translate="no">'+(nameMap[s.id]||s.name.split(' ')[0])+'</td>';
      rows+='<td style="padding:5px 6px;font-size:11px;color:var(--sub)">'+(sh&&sh.start?sh.start:'—')+'</td>';
      rows+='<td style="padding:5px 6px;font-size:11px">'+(ev.clockIn||'—')+'</td>';
      rows+='<td style="padding:5px 4px"><span style="background:'+statusBg+';color:'+statusColor+';padding:2px 7px;border-radius:6px;font-size:10px;font-weight:700">'+statusLabel+'</span></td>';
      rows+='</tr>';
    });
    totalIn+=storeIn;totalBreak+=storeBreak;

    html+='<div style="margin-bottom:14px;border:1px solid var(--border);border-radius:10px;overflow:hidden">';
    html+='<div style="display:flex;align-items:center;justify-content:space-between;padding:8px 12px;background:var(--surface-2);border-bottom:1px solid var(--border)">';
    html+='<div style="font-size:12px;font-weight:700">'+(isQ?'🇶🇦':'🇦🇪')+' '+store+'</div>';
    html+='<div style="display:flex;gap:10px;font-size:11px">';
    html+='<span style="color:var(--rota-green-ink);font-weight:700">'+storeIn+' in</span>';
    if(storeBreak)html+='<span style="color:var(--teal);font-weight:700">'+storeBreak+' break</span>';
    if(storeExpected)html+='<span style="color:#f59e0b;font-weight:700">'+storeExpected+' expected</span>';
    if(storeOff)html+='<span style="color:var(--sub)">'+storeOff+' off</span>';
    html+='</div></div>';
    if(rows){
      html+='<table style="width:100%;border-collapse:collapse"><thead><tr style="background:var(--card)">';
      html+='<th style="padding:4px 6px;font-size:9px;color:var(--sub);text-align:start;font-weight:700;text-transform:uppercase">Staff</th>';
      html+='<th style="padding:4px 6px;font-size:9px;color:var(--sub);text-align:start;font-weight:700;text-transform:uppercase">Scheduled</th>';
      html+='<th style="padding:4px 6px;font-size:9px;color:var(--sub);text-align:start;font-weight:700;text-transform:uppercase">Clocked In</th>';
      html+='<th style="padding:4px 6px;font-size:9px;color:var(--sub);text-align:start;font-weight:700;text-transform:uppercase">Status</th>';
      html+='</tr></thead><tbody>'+rows+'</tbody></table>';
    }
    html+='</div>';
  });

  document.getElementById('cIn').textContent=totalIn;
  document.getElementById('cBreak').textContent=totalBreak;
  document.getElementById('cLate').textContent=totalLate;
  document.getElementById('clockList').innerHTML='<div style="padding:10px 12px">'+html+'</div>';
}

export function renderSingleStoreClock(){
  // Ensure location banner is drawn if applicable
  if (typeof renderLocationBanner === 'function') renderLocationBanner();
  var dateInput=document.getElementById('clockHistoryDate');
  // v80.15: when no date is picked, default to BUSINESS day (03:00–03:00 boundary),
  // not calendar day. So at 00:30 a staffer working a 15:00–00:00 shift still
  // sees Friday's roster and their own clock-in card — they can clock out
  // normally instead of being told "Not yet clocked in" for Saturday's view.
  // History navigation (explicit picker date) keeps calendar semantics.
  var viewingDate=dateInput&&dateInput.value?new Date(dateInput.value):getBusinessDay();
  var isToday=!dateInput||!dateInput.value;
  var today=getDayKey(viewingDate);
  var weekStart=getWeekStart(viewingDate);
  // v79.15.1: localDateStr (NOT toISOString — UTC drift at +4 timezone).
  // Drives getClockEventForDate lookup for past-date renders.
  var viewingDateStr=localDateStr(viewingDate);
  var nowM=new Date().getHours()*60+new Date().getMinutes();
  var inCount=0,breakCount=0,lateCount=0;

  // v48/v123.x: the day's roster = everyone physically scheduled at currentStore
  // today (home staff MINUS anyone covering elsewhere, PLUS covers scheduled
  // here). Shared with the All-Stores (HO) view via buildStoreShiftRoster so the
  // two views can't diverge again — that divergence was the cover-headcount bug.
  var roster = buildStoreShiftRoster(currentStore, today, weekStart);
  var seen = {};
  roster.forEach(function(s){ seen[s.id] = true; });
  // v123.x — include anyone STILL clocked in from a previous business day
  // (carried across the 3am roll-over), even with no shift here today, so they
  // can always reach a Clock Out. Merged into clockEvents by checkStillClockedIn.
  if (isToday) {
    Object.keys(clockEvents).forEach(function(idStr){
      var cid = parseInt(idStr);
      if (seen[cid]) return;
      var cev = clockEvents[cid];
      if (!cev || !cev.carriedOver || cev.status === 'out') return;
      var cs = ALL_STAFF.find(function(x){ return x.id === cid; });
      if (cs) { roster.push(cs); seen[cid] = true; }
    });
  }
  var staff = roster;
  // Disambiguate first names against the day's combined roster (home + covers)
  var displayNames = computeDisplayNames(staff);

  var dateLbl=viewingDate.toLocaleDateString('en-GB',{weekday:'long',day:'numeric',month:'long',year:'numeric'});
  document.getElementById('clockDate').textContent=isToday?dateLbl:dateLbl+' (history)';

  var list=document.getElementById('clockList');
  list.innerHTML='';

  var container=document.createElement('div');
  container.className='clock-cards';

  // Cache for detail sheet — survives the render so openClockDetail can read it
  window._clockCardData = {};

  // v80.16 — first-name disambiguation across today's roster.
  var nameMap = buildFirstNameMap(staff);

  staff.forEach(function(s){
    var sh=getShift(s.id,today,weekStart);
    var color=s.color||COLORS[s.id%COLORS.length];
    var shType=sh?sh.type:'work';
    var displayName = nameMap[s.id] || s.name.split(' ')[0];
    // v123.x — a shift still open from yesterday (carried across the 3am roll)
    // must show a Clock Out regardless of today's shift type.
    var carriedOpen = isToday && clockEvents[s.id] && clockEvents[s.id].carriedOver
      && (clockEvents[s.id].status === 'in' || clockEvents[s.id].status === 'break');

    // Non-working shift types — render a muted card, no action (UNLESS they're
    // still clocked in from yesterday and need to clock out).
    // v129.18: 'apl' added — it fell through to the WORKING-shift branch, so a
    // person on maternity/paternity leave saw "No shift scheduled" with a live
    // Clock In button on their Today screen.
    if(!carriedOpen && (shType==='off'||shType==='toil'||shType==='holiday'||shType==='sick'||shType==='oos'||shType==='apl')){
      var badge={off:'Day off',toil:'TOIL day',holiday:'🌴 Holiday',sick:'🤒 Sick',oos:'Away from store',apl:'🕊️ Authorised Paid Leave'}[shType]||shType;
      var card=document.createElement('div');
      card.className='clock-card is-off is-readonly';
      card.innerHTML=
        // v126.22 (Matt: "by clicking on your circle in clock in") — the avatar
        // circle opens the photo capture pre-picked to this person (PIN still
        // proves identity). stopPropagation keeps the card's own tap intact.
        // v128.52: data-av-* wires the circle into the Avatars sweep — the photo
        // was never appearing because the clock cards predate the chip contract.
        '<div class="cc-avatar" data-av-kind="staff" data-av-id="'+s.id+'" style="background:'+color+';cursor:pointer;overflow:hidden" title="Tap to add your photo" onclick="event.stopPropagation();if(typeof Avatars!==\'undefined\'&&Avatars.openCapture)Avatars.openCapture('+s.id+')">'+initials(s.name)+'</div>'+
        '<div class="cc-body">'+
          '<div class="cc-name"><span translate="no">'+escapeHtml(displayName)+'</span>'+(typeof Achievements!=='undefined'&&Achievements.starFor?Achievements.starFor(s.id):'')+'</div>'+
          '<div class="cc-meta"><span class="cc-chip chip-off">'+badge+'</span></div>'+
        '</div>';
      container.appendChild(card);
      return;
    }

    // Working shift
    // v79.15.1: same fix as renderMultiStoreClock — past dates go through the
    // per-date cache via getClockEventForDate, not getClockForWeek.
    var ev=isToday?(clockEvents[s.id]||{status:'out',log:[]}):(getClockEventForDate(s.id,viewingDateStr)||{});
    var schedText=sh&&sh.start?sh.start+'–'+sh.finish:'No shift scheduled';
    // Late threshold: 5 min after scheduled start (punctuality policy)
    var isLate=isToday&&(ev.status==='out'||!ev.status)&&!ev.clockIn&&sh&&sh.start&&nowM>timeMins(sh.start)+5;

    // Punctuality vs. scheduled start time — drives the secondary chip
    // Thresholds: ≥5 min late = late, ≥10 min early = early, otherwise on-time (no chip)
    var punctClass = '', punctText = '';
    if (sh && sh.start && ev.clockIn) {
      var variance = timeMins(ev.clockIn) - timeMins(sh.start);
      if (variance > 5) {
        punctClass = 'chip-late';
        punctText = variance + ' min late';
      } else if (variance < -10) {
        punctClass = 'chip-early';
        punctText = Math.abs(variance) + ' min early';
      }
      // within window → no chip
    }

    // Compute hours worked (incl. live running time if currently in)
    // Key fixes: skip while on break (status chip covers it), guard against corrupt
    // clock_out < clock_in data (happens with duplicate DB rows from repeated clock-ins)
    var hoursText='';
    if (ev.status === 'break') {
      // On break — no hours text, break status is already shown in chip
      hoursText = '';
    } else if (ev.clockIn && ev.clockOut) {
      var inM = timeMins(ev.clockIn);
      var outM = timeMins(ev.clockOut);
      if (outM > inM) {
        var brkDed = (ev.breakMins != null ? ev.breakMins : (ev.breakDuration || 60));
        var wMin = outM - inM - brkDed;
        if (wMin > 0) hoursText = (wMin/60).toFixed(1) + 'h worked';
      }
    } else if (ev.clockIn && isToday && ev.status === 'in') {
      var wMinLive = nowM - timeMins(ev.clockIn) - (ev.breakMins || 0);
      if (wMinLive < 0 && ev.carriedOver) wMinLive += 24*60; // shift carried across midnight
      if (wMinLive > 0) hoursText = (wMinLive/60).toFixed(1) + 'h so far';
    }

    // Build status chip + action button
    var chip='',dotCls='dot-out',cardCls='',actLabel='Clock In',actCls='act-in',actDisabled=false;
    if(isToday){
      if(ev.status==='in'){
        chip='<span class="cc-chip chip-in">● On floor since '+(ev.clockIn||'?')+(ev.carriedOver?' (yesterday)':'')+'</span>';
        // v123.21 (UX review): name the dominant pay action ("Clock Out", red)
        // instead of a vague grey "Options". The tap still opens the break /
        // clock-out sheet (showClockActionPicker), so Break stays one tap away and
        // the sheet doubles as a confirm before the shift actually ends.
        dotCls='dot-in'; cardCls='is-in'; actLabel='Clock Out'; actCls='act-out';
        inCount++;
      } else if(ev.status==='break'){
        chip='<span class="cc-chip chip-break">⏸ On break since '+(ev.breakStart||'?')+'</span>';
        dotCls='dot-break'; cardCls='is-break'; actLabel='End Break'; actCls='act-break';
        breakCount++;
      } else if(ev.clockIn&&ev.clockOut){
        chip='<span class="cc-chip chip-out">Clocked out at '+ev.clockOut+'</span>';
        dotCls='dot-out'; actLabel='Clock In'; actCls='act-in';
      } else if(isLate){
        chip='<span class="cc-chip chip-late">🔴 Late — scheduled '+sh.start+'</span>';
        dotCls='dot-late'; cardCls='is-late'; actLabel='Clock In'; actCls='act-late';
        lateCount++;
      } else {
        chip='<span class="cc-chip chip-out">Not yet clocked in</span>';
      }
      // Append punctuality chip if notable (only when actually clocked in)
      if (punctText && (ev.status === 'in' || ev.status === 'break' || (ev.clockIn && ev.clockOut))) {
        chip += ' <span class="cc-chip '+punctClass+'" style="margin-inline-start:4px">'+punctText+'</span>';
      }
    } else {
      // Historical view — status snapshot, no action button
      if(ev.clockIn&&ev.clockOut){
        chip='<span class="cc-chip chip-out">'+ev.clockIn+' → '+ev.clockOut+'</span>';
      } else if(ev.clockIn){
        chip='<span class="cc-chip chip-in">Clocked in '+ev.clockIn+' — no clock-out</span>';
      } else {
        chip='<span class="cc-chip chip-out">No record</span>';
      }
      if (punctText) {
        chip += ' <span class="cc-chip '+punctClass+'" style="margin-inline-start:4px">'+punctText+'</span>';
      }
      actDisabled=true;
    }

    // Cache for detail sheet
    window._clockCardData[s.id] = {
      staff: s, color: color, shift: sh, event: ev, isLate: isLate,
      isToday: isToday, schedText: schedText, hoursText: hoursText,
      punctClass: punctClass, punctText: punctText
    };

    var card=document.createElement('div');
    card.className='clock-card '+cardCls+(actDisabled?' is-readonly':'');
    if(!actDisabled){
      (function(sid){ card.onclick=function(e){
        // Let the action button handle its own click
        if(e.target.classList.contains('cc-action'))return;
        openClockDetail(sid);
      };})(s.id);
    }
    var actBtnHtml = actDisabled ? '' :
      '<button class="cc-action '+actCls+'" data-staff-id="'+s.id+'">'+actLabel+'</button>';

    card.innerHTML=
      // v128.52: avatar hydrates via the Avatars sweep (data-av-*). The status
      // dot moves OUT of the hydrated node — sweep() replaces innerHTML, which
      // was ALSO why the dot could never coexist with a photo. The wrapper is
      // position:relative so the dot keeps its exact bottom-right spot.
      '<div style="position:relative;flex-shrink:0">'+
        '<div class="cc-avatar" data-av-kind="staff" data-av-id="'+s.id+'" style="background:'+color+';cursor:pointer;overflow:hidden" title="Tap to add your photo" onclick="event.stopPropagation();if(typeof Avatars!==\'undefined\'&&Avatars.openCapture)Avatars.openCapture('+s.id+')">'+initials(s.name)+'</div>'+
        '<span class="cc-status-dot '+dotCls+'" aria-hidden="true"></span>'+   // v134.4: decoration; the chip says it in words
      '</div>'+
      '<div class="cc-body">'+
        // v126.20 (Matt): an ACTIVE ⭐ star badge shows right next to the
        // clock-in name (guarded cross-bundle; reason on hover).
        '<div class="cc-name"><span translate="no">'+escapeHtml(displayName)+'</span>'+(typeof Achievements!=='undefined'&&Achievements.starFor?Achievements.starFor(s.id):'')+'</div>'+
        '<div class="cc-meta">'+chip+(hoursText?'<span class="cc-hours">· '+hoursText+'</span>':'')+'</div>'+
        '<div class="cc-meta" style="margin-top:3px;color:var(--sub);font-size:12px">'+schedText+'</div>'+
      '</div>'+
      actBtnHtml;

    if(!actDisabled){
      var actBtn = card.querySelector('.cc-action');
      if(actBtn){
        (function(sid){ actBtn.onclick=function(e){
          e.stopPropagation();
          openTimeCardAction(sid);
        };})(s.id);
      }
    }

    container.appendChild(card);
  });

  if(!staff.length){
    container.innerHTML='<div style="text-align:center;padding:40px 20px;color:var(--sub);font-size:13px">No staff assigned to this store yet.</div>';
  }
  list.appendChild(container);

  document.getElementById('cIn').textContent=inCount;
  document.getElementById('cBreak').textContent=breakCount;
  document.getElementById('cLate').textContent=lateCount;
}
